import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, MagnifyingGlass, Copy, ListChecks } from '@phosphor-icons/react';

const API = `${window.location.origin}/api`;

function CopyFromTaskModal({ taskId, onClose, onCopied }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [copying, setCopying] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const res = await fetch(`${API}/tasks?limit=100`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const items = data.items || data;
        // Only show tasks that have subtasks and aren't the current task
        setTasks(items.filter(t => t.task_id !== taskId && t.subtasks && t.subtasks.length > 0));
      }
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const copySubtasks = async (sourceTaskId) => {
    setCopying(sourceTaskId);
    try {
      const res = await fetch(`${API}/tasks/${taskId}/copy-subtasks/${sourceTaskId}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to copy');
      }
      const data = await res.json();
      if (onCopied) onCopied(data);
      onClose();
    } catch (err) {
      alert(err.message);
    } finally {
      setCopying(null);
    }
  };

  const filtered = search.trim()
    ? tasks.filter(t => t.title.toLowerCase().includes(search.toLowerCase()))
    : tasks;

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-[60]" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-lg max-w-md w-full max-h-[80vh] flex flex-col"
        data-testid="copy-from-task-modal"
      >
        <div className="border-b border-slate-200 p-5 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Copy size={20} weight="duotone" className="text-navy" />
            Copy from Task
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="close-copy-task">
            <X size={22} />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-4 flex-shrink-0">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tasks..."
              className="bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-4 py-2 w-full text-sm text-slate-900 focus:border-navy focus:ring-2 focus:ring-blue-100"
              data-testid="copy-task-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-navy border-t-transparent" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8" data-testid="no-tasks-with-subtasks">
              <p className="text-sm text-slate-500">
                {tasks.length === 0 ? 'No other tasks with sub-tasks found.' : 'No matching tasks.'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((t) => (
                <div
                  key={t.task_id}
                  className="border border-slate-200 rounded-lg overflow-hidden hover:border-blue-200 transition-colors"
                  data-testid={`copy-source-${t.task_id}`}
                >
                  <div
                    className="flex items-center justify-between p-3 cursor-pointer"
                    onClick={() => setExpanded(expanded === t.task_id ? null : t.task_id)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{t.title}</p>
                      <p className="text-xs text-slate-400 flex items-center gap-1">
                        <ListChecks size={12} />
                        {t.subtasks.length} sub-tasks
                        {t.project_client && ` \u00b7 ${t.project_client}`}
                      </p>
                    </div>
                  </div>

                  {expanded === t.task_id && (
                    <div className="px-3 pb-3 border-t border-slate-100 pt-2">
                      <ul className="space-y-1 mb-3">
                        {t.subtasks.map((st, i) => (
                          <li key={i} className="text-xs text-slate-600 flex items-center gap-1.5">
                            <span className="w-1 h-1 rounded-full bg-slate-400 flex-shrink-0" />
                            {st.title}
                          </li>
                        ))}
                      </ul>
                      <button
                        onClick={() => copySubtasks(t.task_id)}
                        disabled={copying === t.task_id}
                        className="w-full bg-navy text-white hover:bg-navy-hover transition-colors font-medium px-3 py-2 rounded-lg text-xs disabled:opacity-50 flex items-center justify-center gap-1.5"
                        data-testid={`copy-btn-${t.task_id}`}
                      >
                        <Copy size={13} weight="bold" />
                        {copying === t.task_id ? 'Copying...' : 'Copy Sub-tasks'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default CopyFromTaskModal;
